import { Implements, Service, ServiceLifetime } from '@gitlab/needle';
import { Logger, withPrefix } from '@gitlab-org/logging';
import type { Disposable } from '@gitlab-org/disposable';
import { formatWakeMessage } from '@gitlab-org/tui';
import {
  WakeScheduler,
  type ScheduledWake,
  type ScheduleWakeResult,
} from '@gitlab-org/workflow-executor/node';
import type { SessionManager } from '../../sessions';

// A task id is assigned by the server and unique only within its session, so the
// pair is the identity. Keying on the task id alone would let one session's pending
// wake shadow another's and refuse it as a duplicate.
const wakeKey = ({ workflowId, taskId }: ScheduledWake): string => `${workflowId}\u0000${taskId}`;

// setTimeout's delay is a signed 32-bit int; anything above fires (nearly) immediately,
// which would turn a far-future wake into an instant one.
const MAX_TIMER_DELAY_MS = 2 ** 31 - 1;

// The CLI's WakeScheduler: one `setTimeout` per accepted wake. A singleton so
// timers survive the per-turn executor; a session switch clears foreign timers.
// SessionManager arrives at attach time, not via DI: a constructor dependency
// would close a cycle (WorkflowActionHandler -> WakeScheduler -> SessionManager
// -> BackendFactory -> Tools -> WorkflowActionHandler).
@Implements(WakeScheduler)
@Service({
  dependencies: [Logger],
  lifetime: ServiceLifetime.Singleton,
})
export class TuiWakeScheduler implements WakeScheduler, Disposable {
  #logger: Logger;

  #sessionManager?: SessionManager;

  #timers = new Map<string, { wake: ScheduledWake; timer: NodeJS.Timeout }>();

  #deliver?: (text: string) => void;

  #sessionSubscription?: Disposable;

  constructor(logger: Logger) {
    this.#logger = withPrefix(logger, '[WakeScheduler]');
  }

  /** Until a delivery target is attached, every `schedule` call is refused. */
  attachDeliveryTarget(sessionManager: SessionManager, deliver: (text: string) => void): void {
    this.#sessionManager = sessionManager;
    this.#deliver = deliver;
    this.#sessionSubscription?.dispose();
    this.#sessionSubscription = sessionManager.onActiveSessionChanged((session) =>
      this.#clearWakesNotBelongingTo(session.sessionId),
    );
  }

  schedule(wake: ScheduledWake): ScheduleWakeResult {
    if (!this.#deliver) {
      return {
        scheduled: false,
        reason: 'this client has no interactive session to deliver wakes into',
      };
    }

    if (this.#timers.has(wakeKey(wake))) {
      return {
        scheduled: false,
        reason: `a notification with task_id ${wake.taskId} is already pending`,
      };
    }

    const delayMs = wake.deadline.getTime() - Date.now();
    if (delayMs > MAX_TIMER_DELAY_MS) {
      return {
        scheduled: false,
        reason: `deadline ${wake.deadline.toISOString()} is too far in the future to schedule`,
      };
    }

    const timer = setTimeout(() => this.#fire(wake), Math.max(0, delayMs));
    timer.unref?.(); // never keep the process alive just for a wake

    this.#timers.set(wakeKey(wake), { wake, timer });
    this.#logger.info(
      `Scheduled wake ${wake.taskId} for workflow ${wake.workflowId} at ${wake.deadline.toISOString()}`,
    );
    return { scheduled: true };
  }

  #fire(wake: ScheduledWake): void {
    this.#timers.delete(wakeKey(wake));

    const activeSessionId = this.#sessionManager?.getActiveSession()?.sessionId;
    if (activeSessionId !== wake.workflowId) {
      this.#logger.info(
        `Dropping wake ${wake.taskId}: workflow ${wake.workflowId} is no longer the active session`,
      );
      return;
    }

    this.#logger.info(`Wake ${wake.taskId} fired for workflow ${wake.workflowId}`);
    this.#deliver?.(
      formatWakeMessage({ taskId: wake.taskId, firedAt: new Date(), message: wake.message }),
    );
  }

  #clearWakesNotBelongingTo(sessionId: string): void {
    for (const [key, { wake, timer }] of this.#timers) {
      if (wake.workflowId !== sessionId) {
        clearTimeout(timer);
        this.#timers.delete(key);
        this.#logger.info(
          `Cleared pending wake ${wake.taskId} for workflow ${wake.workflowId} on session switch`,
        );
      }
    }
  }

  dispose(): void {
    this.#sessionSubscription?.dispose();
    for (const { timer } of this.#timers.values()) {
      clearTimeout(timer);
    }
    this.#timers.clear();
  }
}
